export enum TimerMode {
  QUESTION = 'QUESTION',
  DUPLET = 'DUPLET',
  BLITZ = 'BLITZ',
}

export enum TimerState {
  IDLE = 'IDLE',
  RUNNING = 'RUNNING',
  WRITING_ANSWER = 'WRITING_ANSWER',
  PAUSED = 'PAUSED',
  FINISHED = 'FINISHED',
}

export interface TimerDurationsConfig {
  questionSeconds: number;
  dupletSeconds: number;
  blitzSeconds: number;
  blitzSteps: number;
}

export type TimerDurationsInput = Partial<TimerDurationsConfig>;

export const DEFAULT_TIMER_DURATIONS: TimerDurationsConfig = {
  questionSeconds: 60,
  dupletSeconds: 30,
  blitzSeconds: 20,
  blitzSteps: 3,
};

export const WRITING_ANSWER_SECONDS = 10;

export const MODE_LABELS: Record<TimerMode, string> = {
  [TimerMode.QUESTION]: 'Sual',
  [TimerMode.DUPLET]: 'Duplet',
  [TimerMode.BLITZ]: 'Blitz',
};

export const STEP_ORDINALS = ['İlk', 'İkinci', 'Üçüncü', 'Dördüncü', 'Beşinci', 'Altıncı', 'Yeddinci'];

export const CONTROL_LABELS = {
  start: 'Başlat',
  pause: 'Fasilə',
  nextQuestion: 'Növbəti sual',
  reset: 'Vaxtı sıfırla',
  mode: 'Rejim',
  question: 'Sual',
  dupletStep: 'Duplet',
  blitzStep: 'Blitz',
  discussion: 'Müzakirə',
  writeAnswer: 'Cavabı yazın',
  timeUp: 'Vaxt bitdi',
  writingAnswer: 'Cavabın yazılması üçün vaxt',
  discussionTimeUp: 'Müzakirə üçün ayrılmış vaxt bitdi',
  questionTimeUp: 'Sual üçün ayrılmış vaxt bitdi',
  nextDupletQuestion: 'Dupletin növbəti sualına keç',
  nextBlitzQuestion: 'Blitzin növbəti sualına keç',
  keyboardHint: 'Boşluq: başlat/fasilə · Enter: növbəti sual · M: səs',
  muteOn: 'Səsi söndür',
  muteOff: 'Səsi aç',
  soundSettings: 'Səs tənzimləmələri',
  soundSettingsHint: 'Hansı siqnalların səslənəcəyini seçin',
};

export type TimerSoundCue = 'start' | 'preAlarm' | 'mainEnd' | 'writingEnd';

export type TimerSoundSettings = Record<TimerSoundCue, boolean>;

export const DEFAULT_TIMER_SOUND_SETTINGS: TimerSoundSettings = {
  start: true,
  preAlarm: true,
  mainEnd: true,
  writingEnd: true,
};

export const TIMER_SOUND_LABELS: Record<TimerSoundCue, { title: string; description: string }> = {
  start: { title: 'Başlanğıc siqnalı', description: 'Taymer işə salınanda' },
  preAlarm: { title: '10 saniyə qalıb', description: 'Sual rejimində, vaxtın bitməsinə 10 saniyə qalmış' },
  mainEnd: { title: 'Vaxt bitdi', description: 'Əsas vaxt başa çatanda' },
  writingEnd: { title: 'Cavab yazma vaxtı bitdi', description: 'Əlavə 10 saniyə başa çatanda' },
};

export interface TimerCompletePayload {
  mode: TimerMode;
  phase: 'main' | 'writing';
  questionNumber: number;
  dupletStep: number;
  blitzStep: number;
  isFinalBlitzStep: boolean;
}

export interface TimerQuestionChangePayload {
  mode: TimerMode;
  questionNumber: number;
  dupletStep: number;
  blitzStep: number;
  reason: 'manual_next' | 'mode_change' | 'reset';
}

export const resolveTimerDurations = (input?: TimerDurationsInput): TimerDurationsConfig => {
  const merged: TimerDurationsConfig = {
    ...DEFAULT_TIMER_DURATIONS,
    ...(input ?? {}),
  };

  return {
    questionSeconds: Math.max(5, Math.round(merged.questionSeconds)),
    dupletSeconds: Math.max(5, Math.round(merged.dupletSeconds)),
    blitzSeconds: Math.max(3, Math.round(merged.blitzSeconds)),
    blitzSteps: Math.max(1, Math.round(merged.blitzSteps)),
  };
};

export const getDurationMsForMode = (mode: TimerMode, durations: TimerDurationsConfig): number => {
  if (mode === TimerMode.DUPLET) return durations.dupletSeconds * 1000;
  if (mode === TimerMode.BLITZ) return durations.blitzSeconds * 1000;
  return durations.questionSeconds * 1000;
};

export const formatTimerMs = (ms: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};
